// client/src/api/appointments.js
const API_BASE_URL = '/api';

/**
 * Appointments API service functions
 */

// Create request headers (authentication handled by cookies)
const getHeaders = () => {
    return {
        'Content-Type': 'application/json'
    };
};

/**
 * Fetch all appointments for the authenticated user
 */
export const fetchAppointments = async (startDate = null, endDate = null) => {
    try {
        let url = `${API_BASE_URL}/appointments`;
        
        // Add date range query parameters if provided
        if (startDate && endDate) {
            const params = new URLSearchParams({
                start: startDate,
                end: endDate
            });
            url += `?${params.toString()}`;
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch appointments');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching appointments:', error);
        throw error;
    }
};

/**
 * Fetch appointment statistics
 */
export const fetchAppointmentStats = async () => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments/stats`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch appointment stats');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching appointment stats:', error);
        throw error;
    }
};

/**
 * Fetch upcoming appointments for reminders
 */
export const fetchUpcomingAppointments = async (hours = 24) => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments/upcoming?hours=${hours}`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch upcoming appointments');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching upcoming appointments:', error);
        throw error;
    }
};

/**
 * Fetch appointment by lead ID
 */
export const fetchAppointmentByLead = async (leadId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments/by-lead/${leadId}`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch appointment for lead');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching appointment by lead:', error);
        throw error;
    }
};

/**
 * Fetch a specific appointment by ID
 */
export const fetchAppointment = async (appointmentId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments/${appointmentId}`, {
            method: 'GET',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch appointment');
        }

        return await response.json();
    } catch (error) {
        console.error('Error fetching appointment:', error);
        throw error;
    }
};

/**
 * Create a new appointment
 */
export const createAppointment = async (appointmentData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(appointmentData),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create appointment');
        }

        return await response.json();
    } catch (error) {
        console.error('Error creating appointment:', error);
        throw error;
    }
};

/**
 * Update an existing appointment
 */
export const updateAppointment = async (appointmentId, updateData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments/${appointmentId}`, {
            method: 'PUT',
            headers: getHeaders(),
            body: JSON.stringify(updateData),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update appointment');
        }

        return await response.json();
    } catch (error) {
        console.error('Error updating appointment:', error);
        throw error;
    }
};

/**
 * Send appointment reminder
 */
export const sendAppointmentReminder = async (appointmentId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments/${appointmentId}/send-reminder`, {
            method: 'POST',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to send appointment reminder');
        }

        return await response.json();
    } catch (error) {
        console.error('Error sending appointment reminder:', error);
        throw error;
    }
};

/**
 * Delete an appointment
 */
export const deleteAppointment = async (appointmentId) => {
    try {
        const response = await fetch(`${API_BASE_URL}/appointments/${appointmentId}`, {
            method: 'DELETE',
            headers: getHeaders(),
            credentials: 'include'
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete appointment');
        }

        return await response.json();
    } catch (error) {
        console.error('Error deleting appointment:', error);
        throw error;
    }
};